import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AddPartModal from './AddPartModal';
import { PART_CATEGORIES, getPartCategoryLabel, getPartUnitLabel } from '../../constants/partCategories';
import { IconPlus, IconPackage } from '@tabler/icons-react';

export default function Parts() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'parts'), orderBy('partNumber'));
    const unsub = onSnapshot(q, snap => {
      setParts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = parts.filter(p => {
    if (filter !== 'all' && p.category !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!p.partNumber?.toLowerCase().includes(s) && !p.name?.toLowerCase().includes(s) && !p.manufacturer?.toLowerCase().includes(s)) {
        return false;
      }
    }
    return true;
  });

  return (
    <Layout>
      <PageHeader
        title="Parts library"
        subtitle={parts.length + ' parts in the library'}
        actions={<Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setShowAddModal(true)}>New part</Button>}
      />
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip label="All" active={filter === 'all'} onClick={() => setFilter('all')} count={parts.length} />
          {PART_CATEGORIES.map(cat => (
            <Chip key={cat.code} label={cat.label} active={filter === cat.code} onClick={() => setFilter(cat.code)} count={parts.filter(p => p.category === cat.code).length} />
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <input type="text" placeholder="Search by part number, name or manufacturer..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 420 }} />
        </div>
        {loading ? (
          <div style={{ color: 'var(--color-text-tertiary)', fontSize: 13 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <IconPackage size={36} stroke={1} color="var(--color-text-tertiary)" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {parts.length === 0 ? 'No parts in the library yet' : 'No parts match your filter'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                {parts.length === 0 ? 'Add the first part to get started.' : 'Try adjusting the filter or search.'}
              </div>
            </div>
          </Card>
        ) : (
          <Card padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 180px 140px 80px', padding: '10px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)', fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', gap: 12 }}>
              <div>Part number</div>
              <div>Name</div>
              <div>Manufacturer</div>
              <div>Category</div>
              <div>Unit</div>
            </div>
            {filtered.map((part, i) => (
              <div key={part.id} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 180px 140px 80px', padding: '12px 16px', fontSize: 13, gap: 12, borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{part.partNumber}</div>
                <div>{part.name}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>{part.manufacturer || '—'}</div>
                <div><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{getPartCategoryLabel(part.category)}</span></div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{getPartUnitLabel(part.unit)}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
      {showAddModal && <AddPartModal existingParts={parts} onClose={() => setShowAddModal(false)} />}
    </Layout>
  );
}

function Chip({ label, active, onClick, count }) {
  return (
    <div onClick={onClick} style={{ padding: '5px 12px', borderRadius: 'var(--radius-full)', background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)', color: active ? '#fff' : 'var(--color-text-secondary)', border: active ? 'none' : '0.5px solid var(--color-border-light)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' }}>
      {label}
      <span style={{ fontSize: 10, opacity: active ? 0.85 : 0.5 }}>{count}</span>
    </div>
  );
}
